import { isGapSuggestionKind } from "./findingPlanning.ts";
import { normalizeFindingStatus } from "./findingStatus.ts";
import * as Schema from "effect/Schema";

const Count = Schema.Number.check(Schema.isInt(), Schema.isGreaterThanOrEqualTo(0));
const QueueCounts = Schema.Struct({ total: Count, open: Count, accepted: Count });
/**
 * `gaps` is the part of `suggestions` that is a compliance gap (control
 * amendments and missing documents): those count as findings, the rest as
 * record updates. Optional so an older server's answer still decodes; absent
 * means the split is unknown, so the counts that depend on it are null rather
 * than a guess that would show gap suggestions as record updates.
 */
export const ReviewCountsSchema = Schema.Struct({
  suggestions: QueueCounts,
  findings: QueueCounts,
  gaps: Schema.optionalKey(QueueCounts),
});
export type ReviewCounts = typeof ReviewCountsSchema.Type;
export const sameReviewQueueCounts = (a: ReviewCounts["suggestions"], b: ReviewCounts["suggestions"]) =>
  a === b || (a.total === b.total && a.open === b.open && a.accepted === b.accepted);
export const sameReviewCounts = (a: ReviewCounts | null, b: ReviewCounts | null) =>
  a === b || (a !== null && b !== null && sameReviewQueueCounts(a.suggestions, b.suggestions) && sameReviewQueueCounts(a.findings, b.findings)
    && (a.gaps === b.gaps || (a.gaps !== undefined && b.gaps !== undefined && sameReviewQueueCounts(a.gaps, b.gaps))));

/**
 * A finding still waiting on a decision. Legacy statuses fold the way
 * `normalizeFindingStatus` reads them, so every list and count agrees.
 */
export const isOpenFindingStatus = (status: string): boolean => normalizeFindingStatus(status) === "open";

/**
 * A suggestion still waiting on a decision. `accepted` is a retired stage — it
 * marked a suggestion "in progress" without deciding it — so it waits beside
 * `open`, may still be applied or rejected, and is never a decided record.
 */
export const isOpenSuggestionStatus = (status: string): boolean =>
  status === "open" || status === "accepted";

function addStatus(result: { total: number; open: number; accepted: number }, status: string, count: number) {
  result.total += count;
  if (status === "open" || status === "accepted") result[status] += count;
}

/** Closed and future statuses still contribute to the total present. */
export function countReviewStatuses(rows: ReadonlyArray<{ status: string; count: number }>) {
  const result = { total: 0, open: 0, accepted: 0 };
  for (const row of rows) {
    addStatus(result, row.status, row.count);
  }
  return result;
}
function addFindingStatus(result: { total: number; open: number; accepted: number }, status: string, count: number) {
  result.total += count;
  if (isOpenFindingStatus(status)) result.open += count;
}

/** Findings have no acceptance stage. Keep the retired wire count at zero for older clients. */
export function countFindingStatuses(rows: ReadonlyArray<{ status: string; count: number }>) {
  const result = { total: 0, open: 0, accepted: 0 };
  for (const row of rows) {
    addFindingStatus(result, row.status, row.count);
  }
  return result;
}
export function reviewCountsFromItems(
  findings: ReadonlyArray<{ status: string }>,
  suggestions: ReadonlyArray<{ status: string; kind?: string }>,
): ReviewCounts {
  const suggestionCounts = { total: 0, open: 0, accepted: 0 };
  const gapCounts = { total: 0, open: 0, accepted: 0 };
  const findingCounts = { total: 0, open: 0, accepted: 0 };
  for (const item of suggestions) {
    addStatus(suggestionCounts, item.status, 1);
    if (item.kind !== undefined && isGapSuggestionKind(item.kind)) addStatus(gapCounts, item.status, 1);
  }
  for (const item of findings) addFindingStatus(findingCounts, item.status, 1);
  // Gap counts only when every item says its kind (none at all is a known zero); otherwise unknown.
  return suggestions.every((item) => item.kind !== undefined)
    ? { suggestions: suggestionCounts, findings: findingCounts, gaps: gapCounts }
    : { suggestions: suggestionCounts, findings: findingCounts };
}
/** Suggestions still waiting on a decision: open plus the retired "accepted" stage. */
export const openSuggestionCount = (counts: ReviewCounts) =>
  counts.suggestions.open + counts.suggestions.accepted;
/** Gap suggestions (amendments, missing documents) still waiting: they are findings. Null when unknown. */
export const openGapSuggestionCount = (counts: ReviewCounts): number | null =>
  counts.gaps === undefined ? null : counts.gaps.open + counts.gaps.accepted;
/** Every compliance gap waiting on a decision: findings plus gap suggestions. Null when the split is unknown. */
export const openGapCount = (counts: ReviewCounts): number | null => {
  const gaps = openGapSuggestionCount(counts);
  return gaps === null ? null : counts.findings.open + gaps;
};
/** Housekeeping suggestions waiting: the Record updates count, never an alert. Null when the split is unknown. */
export const pendingRecordUpdateCount = (counts: ReviewCounts): number | null => {
  const gaps = openGapSuggestionCount(counts);
  return gaps === null ? null : openSuggestionCount(counts) - gaps;
};
export const openReviewCount = (counts: ReviewCounts) =>
  openSuggestionCount(counts) + counts.findings.open;
